// react
import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

// components
import { Container } from '@/components/atoms/Container/Container';
import { Icon } from '@/components/atoms/Icon/Icon';
import { ProgressBar } from '@/components/atoms/ProgressBar/ProgressBar';

import { Icons } from '@/components/atoms/Icon/icons';
import {
  GenericFlowProps,
  OnboardingActions,
  toGenericOnboarding,
} from '@/components/pages/OnboardingFlow/OnboardingFlow';

type Colors =
  'red' | 'green' | 'yellow' | 'blue' | 'orange' | 'lime' | 'buttermilk';

const animationDuration = 1000;

export const OnboardingLoading = toGenericOnboarding(
  ({
    config,
    currentIndex = 0,
  }: GenericFlowProps<OnboardingLoadingConfigItem>) => {
    // state
    const [isVisible, setIsVisible] = useState(false);
    const [currentLoadingIndex, setCurrentLoadingIndex] =
      useState(currentIndex);

    // memos
    const currentLoadingConfig = useMemo(
      () => config[currentLoadingIndex],
      [config, currentLoadingIndex],
    );
    const currentConfig = useMemo(
      () => config[currentIndex],
      [config, currentIndex],
    );

    // effects
    useEffect(() => {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setCurrentLoadingIndex(currentIndex);
        setIsVisible(true);
      }, animationDuration / 2);
      return () => clearInterval(timer);
    }, [currentIndex]);

    // guard clause
    if (!currentLoadingConfig) return <></>;
    if (!currentConfig) return <></>;
    return (
      <StyledContainer size='sm' color={currentConfig.color}>
        <Wrapper>
          <ContentWrapper $isVisible={isVisible}>
            {currentConfig?.icon && (
              <ImageWrapper $color={currentLoadingConfig.color}>
                <Icon fontSize={40} icon={currentLoadingConfig.icon} />
              </ImageWrapper>
            )}
            <Text>{currentLoadingConfig.text}</Text>
          </ContentWrapper>
          <ProgressBar
            label=' '
            limit={config.length - 1}
            progress={currentIndex}
            color={currentConfig.color}
          />
        </Wrapper>
      </StyledContainer>
    );
  },
);

const ContentWrapper = styled.div<{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 172px;
  gap: ${(props) => props.theme.spacing[32]};
  ${(props) =>
    !props.$isVisible &&
    css`
      opacity: 0;
      transform: scale(0.8);
    `}
  transition-duration: ${animationDuration / 2}ms;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 0 ${(props) => props.theme.spacing[24]};
`;

const StyledContainer = styled(Container)<{ color: Colors }>`
  background: ${(props) => props.theme.color.cards[props.color]?.primary};
  transition:
    padding 0ms ease-in-out 125ms,
    background ${animationDuration}ms;
  height: unset;
  flex: 1;
`;

const ImageWrapper = styled.div<{ $color: Colors }>`
  width: 72px;
  height: 72px;
  min-width: 72px;
  min-height: 72px;
  max-width: 72px;
  max-height: 72px;
  border-radius: 999999px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.color.cards[props.$color]?.primary};
  background: ${(props) => props.theme.color.cards[props.$color]?.tertiary};
  padding: ${(props) => props.theme.spacing[12]};
`;

const Text = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: center;
`;

export interface OnboardingLoadingConfigItem extends OnboardingActions {
  id: string;
  icon: Icons;
  text: string;
  color: Colors;
}

OnboardingLoading.displayName = 'OnboardingLoading';
export default toGenericOnboarding(OnboardingLoading);
